
import React from "react";
import { CommonOption } from "api/modules/common.option";
import { FormFieldConfig } from "Components/Common/CommonForm";
import { ScreeningMaster, ScreeningStatusType } from "./studyscreening.types";
import { Button, UncontrolledPopover, UncontrolledTooltip } from "reactstrap";
import { Column } from "Components/Common/CommonTableIndexWise";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { formatDate } from "common/service/CommonService";

export const studyScreeningForm = (CRFList: CommonOption[], isCrfDisabled: boolean = true): FormFieldConfig[] => [
    {
        name: "studyscreeningname",
        label: "Screening Name",
        placeholder: "Enter Screening Name",
        required: true,
        rules: {
            required: "Screening Name is required",
        },
    },
    {
        name: "crfid",
        label: "CRF Selection",
        type: "select",
        options: CRFList,
        required: true,
        mutipleAllow: true,
        disabled: isCrfDisabled,
        rules: {
            required: "Please select CRF",
        },
    },

];


export interface CRFScreeningModalProps {
    isOpen: boolean;
    toggle: () => void;
    selectedCrf: any | null;
    onClose: () => void;
    onSave: (answers: any) => void;
    isPending: boolean;
    readonly?: boolean;
    parentBreadcrumbs?: { text: string; link?: string; onclick?: () => void }[];
}


export const decisionCardBase: React.CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    padding: "20px 16px",
    borderRadius: 12,
    border: "2px solid #e9ecef",
    cursor: "pointer",
    transition: "all 0.25s ease",
    background: "#fff",
    userSelect: "none",
};

export const decisionCardSelected = (type: "passed" | "failed"): React.CSSProperties => ({
    ...decisionCardBase,
    borderColor: type === "passed" ? "#0a7c42" : "#c9252d",
    background: type === "passed" ? "#e6f4ea" : "#ffeaea",
    boxShadow:
        type === "passed"
            ? "0 0 0 3px rgba(10,124,66,0.12)"
            : "0 0 0 3px rgba(201,37,45,0.12)",
});

export const decisionIconStyle = (
    type: "passed" | "failed",
    isSelected: boolean
): React.CSSProperties => ({
    fontSize: 32,
    color: isSelected ? (type === "passed" ? "#0a7c42" : "#c9252d") : "#adb5bd",
    transition: "all 0.25s ease",
});

export const getScreeningStatus = (value?: string | null): ScreeningStatusType => {
    const s = value?.toLowerCase()?.trim();
    if (s === "passed") return "passed";
    if (s === "failed") return "failed";
    return "pending";
};

export const getScreeningColumns = ({
    activePopover,
    togglePopover,
    hoveredRowId,
    setHoveredRowId,
    openCrf,
    openDecisionModal,
    hasPermission,
    candecide
}: any): Column[] => [
        {
            key: "studyscreeningname",
            label: "Screening Name",
        },
        {
            key: "createddate",
            label: "Screening Date",
            render: (value: string) => (value ? formatDate(value) : ""),
        },
        {
            key: "expirydate",
            label: "Expiry Date",
            render: (value: string) => (value ? formatDate(value) : ""),
        },
        {
            key: "crf",
            label: "CRF",
            textCenter: true,
            render: (_: any, row: any) => {
                const forms = row.crflist || [];
                const completedCount = forms.filter(
                    (f: any) =>
                        f.crfdetails?.answers &&
                        Object.keys(f.crfdetails.answers).length > 0
                ).length;
                const isAllCompleted =
                    completedCount === forms.length && forms.length > 0;
                const popoverId = `crf-popover-${row.id}`;
                const isHovered = hoveredRowId === row.id;

                return (
                    <div className="d-flex align-items-center justify-content-center" >
                        <div
                            id={popoverId}
                            className="position-relative cursor-pointer d-flex align-items-center justify-content-center"
                            style={{
                                width: "32px",
                                height: "32px",
                                backgroundColor: isAllCompleted ? "#e6f4ea" : "#f0f2f5",
                                borderRadius: "6px",
                                transition: "all 0.3s ease",
                                border: `1px solid ${isAllCompleted ? "#b7dfbf" : "#e9ebec"}`,
                            }
                            }
                            onClick={() => togglePopover(popoverId)}
                            onMouseEnter={() => setHoveredRowId(row.id)}
                            onMouseLeave={() => setHoveredRowId(null)}
                        >
                            <i
                                className={`ri-file-list-3-line ${isAllCompleted ? "text-success" : "text-primary"}`}
                                style={{ fontSize: "16px" }}
                            />
                            {
                                isHovered && (
                                    <span
                                        className="position-absolute badge rounded-pill shadow-sm"
                                        style={{
                                            bottom: "-4px",
                                            right: "-4px",
                                            fontSize: "9px",
                                            padding: "2px 5px",
                                            backgroundColor: "#6c757d",
                                            color: "#fff",
                                            zIndex: 1,
                                        }
                                        }
                                    >
                                        {forms.length}
                                    </span>
                                )}
                        </div>

                        {/* POPOVER FOR CRF ICONS */}
                        <UncontrolledPopover
                            trigger="hover"
                            isOpen={activePopover === popoverId}
                            toggle={() => togglePopover(popoverId)}
                            placement="bottom"
                            target={popoverId}
                            className="crf-popover-custom shadow-lg border-0"
                            style={{ minWidth: "200px" }}
                        >
                            <div className="p-3" >
                                <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2" >
                                    <h6 className="m-0 fs-12 fw-bold text-uppercase" >
                                        CRF List({completedCount} / {forms.length})
                                    </h6>
                                </div>
                                < div
                                    className="d-flex flex-wrap gap-2 pt-1"
                                    style={{ maxWidth: "220px" }}
                                >
                                    {
                                        forms.map((form: any, idx: number) => {
                                            const tooltipId = `crf-btn-${row.id}-${idx}`;
                                            const formName =
                                                form.crfdetails?.title || `Form ${form.crfid}`;
                                            const canViewCrf = hasPermission ? hasPermission(PERMISSION_MODULES.STUDY_SCREENING, PERMISSION_ACTIONS.VIEW) : false;
                                            return (
                                                <React.Fragment key={idx} >
                                                    <CommonButton
                                                        module={PERMISSION_MODULES.STUDY_SCREENING}
                                                        action={PERMISSION_ACTIONS.VIEW}
                                                        id={tooltipId}
                                                        color="link"
                                                        className="p-0 d-flex align-items-center justify-content-center shadow-sm"
                                                        style={{
                                                            width: "30px",
                                                            height: "30px",
                                                            backgroundColor: form.crfdetails?.answers
                                                                ? "#e6f4ea"
                                                                : "#f8f9fa",
                                                            borderRadius: "4px",
                                                            border: form.crfdetails?.answers
                                                                ? "1px solid #b7dfbf"
                                                                : "1px solid #dee2e6",
                                                        }
                                                        }
                                                        onClick={() =>
                                                            openCrf({
                                                                ...row,
                                                                selectedCrfIndex: idx,
                                                                selectedCrfDetails: form,
                                                            })
                                                        }
                                                    >
                                                        <i
                                                            className={`ri-file-text-line ${form.crfdetails?.answers ? "text-success" : "text-primary"}`}
                                                            style={{ fontSize: "14px" }}
                                                        />
                                                    </CommonButton>
                                                    {canViewCrf && (
                                                        <UncontrolledTooltip
                                                            target={tooltipId}
                                                            placement="top"
                                                            fade={false}
                                                            container="body"
                                                        >
                                                            {formName}
                                                        </UncontrolledTooltip>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                </div>
                            </div>
                        </UncontrolledPopover>
                    </div>
                );
            },
        },
        ...candecide ? [
        {
            key: "decision",
            label: "Screening Result",
            textCenter: true,
            cell: (row: ScreeningMaster) => {
                const status = sessionStorage.getItem("active_volunteer_screening_status");

                if (status === `"passed"`) {
                    return (
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                background: "#e6f4ea",
                                color: "#0a7c42",
                                border: "1px solid #b7dfbf",
                                borderRadius: 20,
                                padding: "3px 12px",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                            }
                            }
                        >
                            <i className="ri-checkbox-circle-fill" style={{ fontSize: 13 }
                            } />
                            Eligible
                        </span>
                    );
                }

                if (status === `"failed"`) {
                    return (
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                background: "#ffeaea",
                                color: "#c9252d",
                                border: "1px solid #f5c6c6",
                                borderRadius: 20,
                                padding: "3px 12px",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                            }
                            }
                        >
                            <i className="ri-close-circle-fill" style={{ fontSize: 13 }} />
                            Not Eligible
                        </span>
                    );
                }

                return (
                    <Button
                        size="sm"
                        color="primary"
                        outline
                        className="rounded-pill px-3 d-flex align-items-center gap-1 fw-medium mx-auto"
                        onClick={() => openDecisionModal(row)}
                    >
                        <i className="ri-scales-3-line fs-14" />
                        Decide
                    </Button>
                );
            },
        },
        ]:[]
    ];